import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { AiSuggestion, AiSuggestionEntityType } from '@erp/rpc';

/**
 * Hook to fetch an AI suggestion by ID.
 *
 * Automatically polls every 2 seconds while the suggestion status is "generating"
 * to pick up the final result when the agent completes.
 *
 * @deprecated Use useAiSuggestionByEntity instead for better real-time updates
 */
export function useAiSuggestion(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.ai.suggestions.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
    // Poll every 1 second while status is "generating"
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'generating' ? 1000 : false;
    },
  });

  const suggestion = query.data ?? null;
  const isGenerating = suggestion?.status === 'generating';
  const isFailed = suggestion?.status === 'failed';

  return {
    ...query,
    suggestion,
    isGenerating,
    isFailed,
  } satisfies typeof query & {
    data?: AiSuggestion | null;
    suggestion: AiSuggestion | null;
    isGenerating: boolean;
    isFailed: boolean;
  };
}

/**
 * Hook to fetch the current AI suggestion for an entity (e.g., staged order).
 *
 * This is the preferred hook for real-time updates because:
 * 1. It doesn't require knowing the suggestion ID upfront
 * 2. It automatically picks up new suggestions created in the background
 * 3. It polls every 2 seconds while there's a "generating" suggestion
 *
 * @param entityType - The type of entity (e.g., "staged_customer_purchase_order")
 * @param entityId - The ID of the entity
 * @param enabled - Whether the query is enabled (default: true)
 */
export function useAiSuggestionByEntity(
  entityType: AiSuggestionEntityType | undefined,
  entityId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.ai.suggestions.getByEntity.queryOptions({
      input: entityType && entityId ? { entityType, entityId } : skipToken,
    }),
    enabled: Boolean(entityType) && Boolean(entityId) && enabled,
    // Poll every 1 second while status is "generating" for fast feedback
    // Poll every 5 seconds when there's a suggestion to detect new emails/agent runs
    refetchInterval: (query) => {
      const suggestion = query.state.data?.suggestion;
      const interval = suggestion?.status === 'generating' ? 1000 : 5000;
      // Debug logging - remove after confirming polling works
      console.log('[useAiSuggestionByEntity] refetchInterval:', {
        suggestionId: suggestion?.id,
        status: suggestion?.status,
        interval,
      });
      return interval;
    },
  });

  const suggestion = query.data?.suggestion ?? null;
  const isGenerating = suggestion?.status === 'generating';
  const isFailed = suggestion?.status === 'failed';

  return {
    ...query,
    suggestion,
    isGenerating,
    isFailed,
  } satisfies typeof query & {
    suggestion: AiSuggestion | null;
    isGenerating: boolean;
    isFailed: boolean;
  };
}
